/**
 * B2cSellerDeliverySLA — msite PDP Delivery SLA card for the B2C seller flow
 * (`b2cSellerConfig.isEnabled`).
 *
 * Reuses the multiseller Delivery SLA UI as-is (`DeliverySLAList`); only the
 * data source and the two extra rows below the cards differ:
 *   - "Seller: <name>"          — always
 *   - "Other sellers available" — only when more than one seller is on offer
 * Both sit above the delivery note, matching the B2C design and the desktop
 * B2C card. Options come from the scopedView response for the size + pincode on
 * screen, or from the host when it owns its own card set (the cart sheet, off
 * deliveryChoices).
 *
 * Rendered only when the flag is on, so with it off the msite PDP is untouched.
 */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import Text from '../../atoms/Text';
import properties from '../../../modules/ajio-util/properties';
import DeliverySLAList from './DeliverySLAList';
import {
  SLA_CONTEXT,
  SLA_SURFACE,
  SlaContext,
  SlaSurface,
  getSlaSurfaceScreenNames
} from './constants';
import useDeliveryDetailsViewed from './useDeliveryDetailsViewed';
import luxeScreens from '../../constants/luxeScreenType';
import {
  pushDeliveryReturnDetailsViewedB2c,
  pushOtherSellersAvailableClickedB2c
} from '../../../modules/ga/GTMdatalayer';
import { RawSlaOption, SlaOption } from './types';
import {
  getHasMoreSellers,
  getIsSlaFetching,
  getSlaError,
  getSlaOptionsForSku
} from './selectSlaForSku';
import { getB2cSellerConfig } from '../../selectors/featureFlagsSelector';
import { showModal } from '../../../actions/shared/modal';
import { selectSlaFromViewOption } from '../../../edd/actions';
import { ModalTypes } from '../../../constants/index.js';
import styles from './index.pcss';

const DEFAULT_OTHER_SELLERS_LABEL = 'Other sellers available';

const cx = classNames.bind(styles);

const imageBaseUrl = properties.getProperty('imageBaseUrl');
/** Storefront glyph for the seller row, one per store theme. */
const SELLER_ICON = `${imageBaseUrl}/static/img/b2c-seller-icon.svg`;
const SELLER_ICON_LUXE = `${imageBaseUrl}/static/img/b2c-seller-icon-luxe.svg`;
const SELLER_ICON_FLEEK = `${imageBaseUrl}/static/img/b2c-seller-icon-fleek.svg`;

/** Ajiogram has its own glyph and wins over luxe, matching the row's colours. */
const sellerIconFor = (luxe: boolean, fleek: boolean): string => {
  if (fleek) return SELLER_ICON_FLEEK;
  return luxe ? SELLER_ICON_LUXE : SELLER_ICON;
};

interface B2cSellerDeliverySLAProps {
  /**
   * Raw options to render, when the host has its own card set (the cart sheet's
   * deliveryChoices response). Omitted by the PDP and wishlist, which read the
   * scopedView response for `skuId` out of the store instead.
   */
  slaOptions?: RawSlaOption[] | null;
  /** Seller row copy, when the host names a different seller than the cards do. */
  sellerName?: string;
  /**
   * From the API's `hasMoreSellers`. Only then is there a list worth opening,
   * so the "Other sellers available" link is gated on it.
   */
  hasMoreSellers?: boolean;
  /**
   * Heading above the cards. The pincode card supplies its own ("Delivery &
   * Return Details"), so it goes without; the size bottom sheet passes
   * "Delivery Details", which also brings the info tooltip.
   */
  title?: string;
  /** Where the card sits. Cart suffixes the amounts with "/ pc". */
  context?: SlaContext;
  /**
   * Fired when the user picks a different seller. The cart uses it to enable
   * Update; the PDP and wishlist have nothing to commit, so they omit it.
   */
  onSelectOption?: (option: SlaOption) => void;
  /**
   * SKU whose scopedView response to render. Defaults to the PDP's selected
   * size; hosts that keep their own size state (wishlist / cart sheets) pass it.
   */
  skuId?: string;
  /** luxe theme, forwarded from the pincode card */
  luxe?: boolean;
  /** Ajiogram (fleek) store theme */
  fleek?: boolean;
  /**
   * Size / bag / wishlist drawers sit on a lighter fleek surface than the page.
   * Forwarded to DeliverySLAList so B2C rows pick up the same drawer tint as
   * the multiseller cards (`sla-list--fleek-dialog`).
   */
  onDialogSurface?: boolean;
  /**
   * Which screen this card is mounted on. Only names the screen for analytics —
   * the PDP card and the PDP size half-card are one `context` but two screen
   * views, and the bag / wishlist sheets report their own screen source.
   */
  surface?: SlaSurface;
  /**
   * Already-mapped analytics product item, from hosts that own their own entity
   * mapping (the cart / wishlist sheets, off `getProductItemForCart`). The PDP
   * surfaces omit it and the PDP mapping is used instead.
   */
  productItem?: Record<string, unknown> | null;
  /**
   * Size label for `size_selected` on the event, from hosts that keep their own
   * size state (the bag / wishlist sheets). PDP surfaces omit it and the product
   * slice's selected size is used.
   */
  sizeSelectedName?: string;
}

const B2cSellerDeliverySLA: FC<B2cSellerDeliverySLAProps> = ({
  slaOptions,
  sellerName,
  hasMoreSellers,
  title,
  context = SLA_CONTEXT.PDP,
  onSelectOption,
  skuId,
  luxe = false,
  fleek = false,
  onDialogSurface = false,
  surface = SLA_SURFACE.PDP,
  productItem = null,
  sizeSelectedName
}) => {
  const dispatch = useDispatch();
  const b2cSellerConfig = useSelector((state: any) => getB2cSellerConfig(state)) || {};
  // B2C card copy comes from its own flag, never multiSellerSlaConfig.
  const priceTypeTags = b2cSellerConfig?.priceTypeTags;
  const slaCodeLabels = b2cSellerConfig?.slaTimeMessage;
  const otherSellersLabel =
    b2cSellerConfig?.otherSellersLabel || DEFAULT_OTHER_SELLERS_LABEL;

  // scopedView response for the size + pincode on screen (fetchAllSellers=false),
  // stored by fetchDeliveryDetailsV2 — same slice the multiseller cards read.
  const activePincode = useSelector((state: any) => state?.edd?.activePincode);
  const pdpSizeCode = useSelector((state: any) => state?.product?.selectedSizeCode || '');
  const selectedSizeCode = skuId || pdpSizeCode;
  const storeSlaOptions = useSelector((state: any) =>
    getSlaOptionsForSku(state, activePincode, selectedSizeCode)
  );
  const isLoading = useSelector((state: any) =>
    getIsSlaFetching(state, activePincode, selectedSizeCode)
  );
  const error = useSelector((state: any) => getSlaError(state, activePincode, selectedSizeCode));
  const storeHasMoreSellers = useSelector((state: any) =>
    getHasMoreSellers(state, activePincode, selectedSizeCode)
  );

  // The PDP and wishlist hand over nothing and read the scopedView response from
  // the store; the cart sheet owns its deliveryChoices card set and passes it in.
  const hostOwnsOptions = Array.isArray(slaOptions);

  /**
   * Snapshot of everything the card shows, taken when the All Sellers sheet
   * opens and held until it closes.
   *
   * The sheet changes the page under itself — its size strip is the PDP's own,
   * so `selectedSizeCode` moves on (with no scopedView call for this card, by
   * design) and the picked seller can move too. None of that should reach the
   * card behind the sheet: closing re-scopes it, and that response is the one
   * worth rendering. Frozen wholesale so the cards, seller row and "Other
   * sellers" link can never disagree with each other.
   */
  const sellerListOpen = useSelector((state: any) =>
    Boolean(state?.modals?.[ModalTypes.B2C_SELLER_LIST])
  );
  const frozenRef = useRef<{
    options: RawSlaOption[];
    hasMoreSellers: boolean;
  } | null>(null);
  // Host-owned card sets never re-scope behind the sheet, so there is nothing
  // to freeze for them.
  if (!hostOwnsOptions) {
    if (sellerListOpen) {
      if (!frozenRef.current) {
        frozenRef.current = { options: storeSlaOptions, hasMoreSellers: storeHasMoreSellers };
      }
    } else if (frozenRef.current) {
      frozenRef.current = null;
    }
  }
  const frozen = hostOwnsOptions ? null : frozenRef.current;

  const shownOptions = hostOwnsOptions ? slaOptions : frozen?.options ?? storeSlaOptions;
  const shownHasMoreSellers = hostOwnsOptions
    ? Boolean(hasMoreSellers)
    : frozen
    ? frozen.hasMoreSellers
    : storeHasMoreSellers;

  const deliveryNote = b2cSellerConfig?.deliverySlaNote || '';
  // The note is rendered by DeliverySLAList; DeliverySLAList falls back to the
  // multiseller config when handed an empty one, so only pass a non-empty value.
  const footnote = useMemo(() => {
    if (!deliveryNote) return undefined;
    return deliveryNote.startsWith('*') ? deliveryNote : `*${deliveryNote}`;
  }, [deliveryNote]);

  /**
   * Seller shown under the cards follows the card the user is actually on.
   *
   * The pick is held here rather than read back off `shownOptions`: those carry
   * the API's `isSelected`, which does not move when the user taps another card
   * (the selection lives inside DeliverySLAList). Reset whenever a new response
   * arrives, so a stale name cannot outlive the options it came from.
   */
  const [pickedSellerName, setPickedSellerName] = useState('');
  useEffect(() => {
    // Functional form so an unchanged name bails out of the render entirely —
    // a `shownOptions` whose identity churns would otherwise re-render on every
    // effect run and trip React's update-depth limit.
    setPickedSellerName(previous => (previous ? '' : previous));
  }, [shownOptions]);

  // Host-supplied name wins, then the user's pick, then whatever the API marked.
  const selectedSellerName = useMemo(() => {
    if (sellerName) return sellerName;
    if (pickedSellerName) return pickedSellerName;
    const list = Array.isArray(shownOptions) ? shownOptions : [];
    return (list.find(option => option?.isSelected) || list[0] || {})?.sellerName || '';
  }, [sellerName, pickedSellerName, shownOptions]);

  const deferredPickRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (deferredPickRef.current) window.clearTimeout(deferredPickRef.current);
    },
    []
  );

  /**
   * "Delivery And Return Details Viewed", B2C cohort. The multiseller emitters
   * in the pincode cards stand down when this flag is on (the card they were
   * reporting against is this one), so the event is pushed from here instead —
   * covering every msite mount in one place: PDP, the PDP size half-card,
   * ajiogram PDP and the bag / wishlist size sheets. The surface only names the
   * screen — see `getSlaSurfaceScreenNames`.
   */
  const productData = useSelector((state: any) => state?.product?.productDetails);
  const pdpSizeName = useSelector((state: any) => state?.product?.selectedSizeName || '');
  const slaDetailsBySku = useSelector(
    (state: any) => (activePincode && state?.edd?.slaDetails?.[activePincode]) || null
  );
  const isPdpSurface = surface === SLA_SURFACE.PDP || surface === SLA_SURFACE.SIZE_HALF_CARD;
  const { ref: viewedRef } = useDeliveryDetailsViewed({
    // While the All Sellers sheet covers the card, the card's own intersection
    // can still read as visible under the portal — hold the event until it closes.
    enabled: !sellerListOpen,
    // A host-owned card set (the bag sheet's deliveryChoices response) has no
    // fetch of its own here, so the store's fetching flag says nothing about it.
    ready:
      (hostOwnsOptions || !isLoading) && Array.isArray(shownOptions) && shownOptions.length > 0,
    resetKeys: [activePincode, selectedSizeCode],
    payload: () =>
      pushDeliveryReturnDetailsViewedB2c({
        platform: 'msite',
        screenNames: getSlaSurfaceScreenNames(surface, {
          luxePdpScreenName: luxeScreens?.pdp?.screenName || ''
        }),
        pincode: activePincode,
        sizeSelected: sizeSelectedName || pdpSizeName,
        sizeSelectedFlag: Boolean(selectedSizeCode),
        slaOptions: shownOptions,
        slaDetailsBySku,
        deliveryInfoText: deliveryNote,
        otherSellersAvailable: shownHasMoreSellers,
        productItem,
        // PDP surfaces only: off the bag / wishlist sheets the product slice
        // holds whatever PDP was last visited, not the line on screen — those
        // hosts pass `productItem` instead.
        productData: isPdpSurface ? productData : null
      })
  });

  /**
   * Applies the picked option's price to the product slice and, under B2C, puts
   * the CTA back to "Add to Bag" — the bag line is tied to the option that was
   * selected when it was added. PDP only: the cart / wishlist hosts commit their
   * own line and must not move PDP state.
   */
  const handleSelectOption = (option: SlaOption): void => {
    // Paints with the radio — this is the row the user just picked.
    setPickedSellerName(option?.sellerName || '');
    if (deferredPickRef.current) window.clearTimeout(deferredPickRef.current);
    deferredPickRef.current = window.setTimeout(() => {
      deferredPickRef.current = null;
      if (context === SLA_CONTEXT.PDP) {
        dispatch(selectSlaFromViewOption(option));
      }
      onSelectOption?.(option);
    }, 0);
  };

  /**
   * "Other Seller Available Clicked", then open the sheet. Reported against the
   * cards as they stand at the tap — the snapshot freeze above starts on the
   * same state change, so reading `shownOptions` here is what the user saw.
   */
  const handleOtherSellersClick = (): void => {
    pushOtherSellersAvailableClickedB2c({
      screenNames: getSlaSurfaceScreenNames(surface, {
        luxePdpScreenName: luxeScreens?.pdp?.screenName || ''
      }),
      slaOptions: shownOptions,
      sizeSelected: sizeSelectedName || pdpSizeName,
      productItem,
      // Same split as the viewed event: off the bag / wishlist sheets the
      // product slice is not the line on screen, so those hosts pass
      // `productItem` and this stays null.
      productData: isPdpSurface ? productData : null
    });
    dispatch(showModal(ModalTypes.B2C_SELLER_LIST));
  };

  const sellerRows = (
    <>
      {selectedSellerName && (
        <div className={cx('sla-seller')}>
          <img
            className={cx('sla-seller__icon')}
            src={sellerIconFor(luxe, fleek)}
            alt=""
            aria-hidden="true"
          />
          <Text variant="T2" color={fleek ? 'White' : luxe ? 'Light_Black' : 'Nero'} removeGrouping>
            {`Seller: ${selectedSellerName}`}
          </Text>
        </div>
      )}
      {shownHasMoreSellers && (
        <button
          type="button"
          className={cx('sla-other-sellers')}
          onClick={handleOtherSellersClick}
        >
          {otherSellersLabel}
        </button>
      )}
    </>
  );

  return (
    // Plain wrapper, no styles of its own: the observed element for
    // "Delivery And Return Details Viewed". The card has no stable id and is
    // portalled in some of its mounts, so a ref is what can be observed.
    <div ref={viewedRef}>
      <DeliverySLAList
        slaOptions={shownOptions}
        priceTypeTags={priceTypeTags}
        slaCodeLabels={slaCodeLabels}
        // A host-owned card set has no fetch of its own, and while the sheet holds
        // the snapshot there is nothing in flight either — neither state applies.
        isLoading={hostOwnsOptions || frozen ? false : isLoading}
        error={hostOwnsOptions || frozen ? false : error}
        context={context}
        onSelectOption={handleSelectOption}
        title={title}
        // With a title the list shows an info icon beside it; the note reads as
        // the tooltip there and stays as the footnote under the cards.
        titleTooltip={title ? deliveryNote : undefined}
        footnote={footnote}
        footerSlot={sellerRows}
        b2c
        luxe={luxe}
        fleek={fleek}
        onDialogSurface={onDialogSurface}
      />
    </div>
  );
};

export default B2cSellerDeliverySLA;
